/**
 * FunnelToast
 *
 * Bridges funnel `experienceType: "toast"` into the host toast queue.
 * Extracts a short message from pageContent / offer and enqueues via showToast.
 * Keeps the funnel experience mounted (like `none`) so ActionExecutor /
 * ListenerBinder / StepReactions can still run.
 */

import { useEffect, useRef } from 'react';
import type { FunnelToastVariant } from '@template-project/funnels-contract';
import type { FunnelOffer, StepPageContent, StepPageBlock, DisplaySettings } from '../types';
import { normalizeBlocks } from './BlockRenderer';
import { useFunnelContext } from '../funnel-provider';

export type { FunnelToastVariant };

interface FunnelToastProps {
  offer: FunnelOffer;
  pageContent?: StepPageContent | StepPageBlock[] | null;
  displaySettings?: DisplaySettings | null;
}

function readBlockText(block: StepPageBlock): string {
  const props = (block.props ?? {}) as Record<string, unknown>;
  for (const key of ['content', 'text', 'headline', 'title', 'message'] as const) {
    const value = props[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
}

export function extractToastMessage(
  pageContent: StepPageContent | StepPageBlock[] | null | undefined,
  offer: FunnelOffer,
): string {
  const blocks = normalizeBlocks(pageContent);
  for (const block of blocks) {
    if (
      block.type === 'text' ||
      block.type === 'rich_text' ||
      block.type === 'headline' ||
      block.type === 'popup-headline'
    ) {
      const text = readBlockText(block);
      if (text) return text;
    }
  }
  for (const block of blocks) {
    const text = readBlockText(block);
    if (text) return text;
  }
  if (offer.headline?.trim()) return offer.headline.trim();
  if (offer.description?.trim()) return offer.description.trim();
  if (offer.name?.trim()) return offer.name.trim();
  return '';
}

function resolveToastVariant(
  displaySettings?: DisplaySettings | null,
): FunnelToastVariant {
  const variant = displaySettings?.toastVariant;
  // `info` is the default; only special-case the other authored variants.
  if (variant === 'success' || variant === 'error' || variant === 'warning') {
    return variant;
  }
  return 'info';
}

export function FunnelToast({
  offer,
  pageContent,
  displaySettings,
}: FunnelToastProps) {
  const { showToast } = useFunnelContext();
  // Latch on payload identity so Strict Mode / effect re-runs don't
  // double-enqueue, but a later toast step with new content still fires.
  const lastFiredKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const message = extractToastMessage(pageContent, offer);
    if (!message || !showToast) return;

    const variant = resolveToastVariant(displaySettings);
    const duration = displaySettings?.toastDurationMs;
    const key = `${variant}\0${duration ?? ''}\0${message}`;
    if (lastFiredKeyRef.current === key) return;
    lastFiredKeyRef.current = key;

    showToast({
      message,
      variant,
      duration,
    });
  }, [displaySettings, offer, pageContent, showToast]);

  return null;
}
